import React, { useMemo, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, GraduationCap, FileText, Layers, BookOpen, MessageSquare } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { FileCard } from '../components/FileCard';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
const SUBJECTS = ['All Subjects', 'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Kinyarwanda', 'French', 'History', 'Geography', 'Economics', 'Entrepreneurship', 'Computer Science', 'General Studies'];
const LEVELS = ['All Levels', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'Primary', 'University'];
export function Marketplace() {
  const {
    t
  } = useLanguage();
  const {
    files,
    getFileData
  } = useData();
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'book' | 'pastpaper' | 'other'>('all');
  const [subject, setSubject] = useState('All Subjects');
  const [level, setLevel] = useState('All Levels');
  const filteredFiles = useMemo(() => {
    return files.filter(f => {
      const matchesSearch = f.name.toLowerCase().includes(search.toLowerCase()) || f.description.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === 'all' || f.type === filter;
      const matchesSubject = subject === 'All Subjects' || (f.subject || '').toLowerCase() === subject.toLowerCase();
      const matchesLevel = level === 'All Levels' || (f.level || '').toLowerCase() === level.toLowerCase();
      return matchesSearch && matchesFilter && matchesSubject && matchesLevel;
    });
  }, [files, search, filter, subject, level]);
  const purchasedIds = currentUser?.purchasedFiles || [];
  const handleBuy = (file: {
    id: string;
  }) => {
    if (!currentUser) {
      toast.error(t('chat.login.required'));
      navigate('/auth');
      return;
    }
    navigate(`/payment/${file.id}`);
  };
  const handleDownload = async (file: {
    id: string;
    name: string;
    fileFormat: string;
  }) => {
    toast.info(`Preparing download: ${file.name}...`);
    const data = await getFileData(file.id);
    if (data) {
      const a = document.createElement('a');
      a.href = data;
      a.download = `${file.name}.${file.fileFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`${t('market.download')}: ${file.name}`);
    } else {
      const blob = new Blob([`This is a simulated download of: ${file.name}\nFormat: ${file.fileFormat}\n\nThank you for purchasing from Theo EduMarket Rwanda!`], {
        type: 'text/plain'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.name}.${file.fileFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${t('market.download')}: ${file.name}`);
    }
  };
  const filters = [{
    key: 'all' as const,
    label: t('market.filter.all'),
    icon: Layers
  }, {
    key: 'book' as const,
    label: t('market.filter.books'),
    icon: GraduationCap
  }, {
    key: 'pastpaper' as const,
    label: t('market.filter.pastpapers'),
    icon: FileText
  }, {
    key: 'other' as const,
    label: t('market.filter.other'),
    icon: Filter
  }];
  // Get unique subjects and levels from files
  const availableSubjects = useMemo(() => {
    const subjects = new Set(files.map(f => f.subject).filter(Boolean));
    return ['All Subjects', ...Array.from(subjects)] as string[];
  }, [files]);
  const availableLevels = useMemo(() => {
    const levels = new Set(files.map(f => f.level).filter(Boolean));
    return ['All Levels', ...Array.from(levels)] as string[];
  }, [files]);
  return <div className="w-full min-h-screen flex flex-col" data-app="theo-edumarket" data-id="element-887">
      <div className="flex-1" data-id="element-888">
        {/* Header */}
        <section className="bg-gradient-to-r from-blue-700 via-purple-700 to-blue-800 text-white py-6 sm:py-10" data-id="element-889">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8" data-id="element-890">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-891">
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2" data-id="element-892">
                {t('market.title')}
              </h1>
              <p className="text-blue-100 text-xs sm:text-sm mb-4 sm:mb-6" data-id="element-893">
                {t('app.description')}
              </p>

              {/* Search */}
              <div className="relative max-w-xl w-full" data-id="element-894">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-300" data-id="element-895" />
                <Input placeholder={t('market.search')} value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-200 focus:bg-white/20" data-id="element-896" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filters + Grid */}
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-4 sm:py-8" data-id="element-897">
          {/* Filter Tabs */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="space-y-3 mb-4 sm:mb-6" data-id="element-898">
            {/* Type filters */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-899">
              {filters.map(f => <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f.key)} className={`gap-1.5 text-xs sm:text-sm ${filter === f.key ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`} data-id="element-900">
                  <f.icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" data-id="element-901" />
                  {f.label}
                  {f.key !== 'all' && <Badge variant="secondary" className="ml-0.5 text-[10px] sm:text-xs h-4 sm:h-5 px-1 sm:px-1.5" data-id="element-902">
                      {files.filter(file => f.key === 'all' || file.type === f.key).length}
                    </Badge>}
                </Button>)}
            </div>

            {/* Subject + Level filters */}
            <div className="flex flex-wrap gap-2" data-id="element-903">
              <Select value={subject} onValueChange={setSubject} data-id="element-904">
                <SelectTrigger className="w-[160px] sm:w-[180px] h-8 text-xs sm:text-sm" data-id="element-905">
                  <BookOpen className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" data-id="element-906" />
                  <SelectValue data-id="element-907" />
                </SelectTrigger>
                <SelectContent data-id="element-908">
                  {(availableSubjects.length > 1 ? availableSubjects : SUBJECTS).map(s => <SelectItem key={s} value={s} data-id="element-909">
                      {s}
                    </SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={level} onValueChange={setLevel} data-id="element-910">
                <SelectTrigger className="w-[130px] sm:w-[150px] h-8 text-xs sm:text-sm" data-id="element-911">
                  <GraduationCap className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" data-id="element-912" />
                  <SelectValue data-id="element-913" />
                </SelectTrigger>
                <SelectContent data-id="element-914">
                  {(availableLevels.length > 1 ? availableLevels : LEVELS).map(l => <SelectItem key={l} value={l} data-id="element-915">
                        {l}
                      </SelectItem>)}
                </SelectContent>
              </Select>

              {(subject !== 'All Subjects' || level !== 'All Levels') && <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => {
              setSubject('All Subjects');
              setLevel('All Levels');
            }} data-id="element-916">
                  Clear filters
                </Button>}
            </div>
          </motion.div>

          {/* Results count */}
          <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4" data-id="element-917">
            {filteredFiles.length}{' '}
            {filteredFiles.length === 1 ? 'result' : 'results'}
          </p>

          {/* Grid */}
          {filteredFiles.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-id="element-918">
              {filteredFiles.map(file => <FileCard key={file.id} file={file} purchased={purchasedIds.includes(file.id)} onBuy={() => handleBuy(file)} onDownload={() => handleDownload(file)} data-id="element-919" />)}
            </div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-12 sm:py-16" data-id="element-920">
              <motion.div animate={{
            y: [0, -6, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity
          }} data-id="element-921">
                <GraduationCap className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground/50 mx-auto mb-3" data-id="element-922" />
              </motion.div>
              <p className="text-sm text-muted-foreground" data-id="element-923">
                {t('market.empty')}
              </p>
            </motion.div>}

          {/* WhatsApp floating button */}
          <a href="https://wa.me/250783138128?text=Hello%20Theo%20EduMarket%20Rwanda%2C%20I%20need%20help" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40" data-id="element-924">
            <motion.div whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.95
          }} className="h-14 w-14 rounded-full bg-green-500 shadow-lg shadow-green-500/30 flex items-center justify-center" data-id="element-925">
              <MessageSquare className="h-6 w-6 text-white" data-id="element-926" />
            </motion.div>
          </a>
        </div>
      </div>
      <Footer data-id="element-927" />
    </div>;
}